import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { FOCUS_RING_CLASS } from './focus-ring'

/**
 * `Button` - the single button primitive.
 *
 * It exists because the same brand-fill and outline Tailwind strings were
 * hand-copied across eleven call sites, and the focus-ring pairing was
 * re-decided at each one. That is how the ring rules drifted. Variants and
 * the ring belong in one place.
 *
 * Variants map to the design handoff's three button treatments:
 *
 *   `primary`   accent fill, white label. One per view, for the main action.
 *   `secondary` outline on surface, hovering to `bg-surface-2`.
 *   `danger`    soft danger fill. Destructive or escalating actions only.
 *
 * Sizes: `md` (36px) is the handoff's standard control height; `sm` (32px)
 * is for controls sitting inside a dense row, such as the table footer
 * pager. Both clear the 24x24 WCAG 2.2 target-size floor with room to
 * spare.
 *
 * Renders a `<button>` by default, or a react-router `<Link>` when `to` is
 * supplied - so a navigation action that looks like a button is still a
 * real link (right-click, middle-click and copy-link all keep working).
 */

export type ButtonVariant = 'primary' | 'secondary' | 'danger'
export type ButtonSize = 'sm' | 'md'

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-brand-primary text-text-on-brand hover:bg-brand-primary-hover active:bg-brand-primary-active',
  secondary:
    'border border-border-default bg-bg-surface text-text-primary hover:bg-bg-surface-2 active:bg-bg-surface-2',
  danger: 'border border-status-danger bg-status-danger-bg text-status-danger hover:brightness-95',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-murmuration-3 text-murmuration-body',
  md: 'h-9 px-murmuration-4 text-murmuration-body-strong',
}

const BASE =
  'inline-flex select-none items-center justify-center gap-murmuration-2 rounded-md no-underline transition-colors duration-murmuration-fast disabled:cursor-not-allowed disabled:opacity-60 hover:no-underline'

function classesFor(variant: ButtonVariant, size: ButtonSize, className?: string): string {
  return [BASE, VARIANT[variant], SIZE[size], FOCUS_RING_CLASS, className ?? '']
    .filter(Boolean)
    .join(' ')
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly children: ReactNode
  /** Escape hatch for layout only (margins, width). Never for colour. */
  readonly className?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', type = 'button', className, children, ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} className={classesFor(variant, size, className)} {...rest}>
      {children}
    </button>
  )
})

export interface ButtonLinkProps {
  readonly to: string
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  readonly children: ReactNode
  readonly className?: string
}

/** A `<Link>` styled as a button. Use for navigation, not for actions. */
export function ButtonLink({
  to,
  variant = 'secondary',
  size = 'md',
  className,
  children,
}: ButtonLinkProps): JSX.Element {
  return (
    <Link to={to} className={classesFor(variant, size, className)}>
      {children}
    </Link>
  )
}
